import React, {FC} from 'react'
import Loader from 'src/utils/Loader'
import {Button, Form, Modal, Spinner} from 'react-bootstrap'
import {Link} from 'react-router-dom'

export type PackageFormState = {
  length: string
  width: string
  height: string
  weight: string
  weightUnit: string
  dimensionUnit: string
}

type Props = {
  show: boolean
  loading: boolean
  saving: boolean
  mode: 'create' | 'update'
  order: any
  lineItems: any[]
  form: PackageFormState
  onClose: () => void
  onSave: () => void
  onFormChange: (next: PackageFormState) => void
}

type UnitOption = {
  value: string
  label: string
}

const DEFAULT_WEIGHT_UNITS: UnitOption[] = [
  {value: 'KGS', label: 'KG'},
  {value: 'GMS', label: 'GM'},
  {value: 'LBS', label: 'LB'},
  {value: 'OZS', label: 'OZ'},
  {value: 'MGS', label: 'MG'},
  {value: 'TONS', label: 'TN'},
  {value: 'MLS', label: 'ML'},
]
const DEFAULT_DIMENSION_UNITS = ['MM', 'CM', 'M', 'IN', 'FT', 'YD']

const ensureOption = (options: string[], value: string) => {
  if (!value || options.includes(value)) return options
  return [value, ...options]
}

const ensureWeightOption = (options: UnitOption[], value: string): UnitOption[] => {
  if (!value) return options
  if (options.some((option) => option.value === value)) return options
  return [{value, label: value.slice(0, 2)}, ...options]
}

const OrderPackageModal: FC<Props> = ({
  show,
  loading,
  saving,
  mode,
  order,
  lineItems,
  form,
  onClose,
  onSave,
  onFormChange,
}) => {
  const orderCode = order?.api_code || order?.api_id || 'N/A'
  const weightUnits = ensureWeightOption(DEFAULT_WEIGHT_UNITS, form.weightUnit)
  const dimensionUnits = ensureOption(DEFAULT_DIMENSION_UNITS, form.dimensionUnit)

  return (
    <Modal
      show={show}
      onHide={onClose}
      centered
      size='xl'
      backdrop='static'
      keyboard={!saving}
    >
      <Modal.Header closeButton={!saving}>
        <Modal.Title>
          {mode === 'update' ? 'Update' : 'Create'} Package for Order #
          {order?.id ? (
            <Link className='ms-1 text-decoration-underline' to={`/sales/details/${order.id}`}>
              {orderCode}
            </Link>
          ) : (
            <span className='ms-1'>{orderCode}</span>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{minHeight: '260px'}}>
        {loading ? (
          <div className='d-flex justify-content-center align-items-center py-8'>
            <Loader variant="section" />
          </div>
        ) : (
          <>
            <div className='row g-3 align-items-end'>
              <div className='col-lg-8'>
                <label className='form-label fw-bold mb-2'>Dimensions (L x W x H)</label>
                <div className='d-flex align-items-center gap-2'>
                  <Form.Control
                    type='number'
                    min='0'
                    step='0.01'
                    placeholder='L'
                    value={form.length}
                    onChange={(e) => onFormChange({...form, length: e.target.value})}
                  />
                  <span className='fw-bold'>x</span>
                  <Form.Control
                    type='number'
                    min='0'
                    step='0.01'
                    placeholder='W'
                    value={form.width}
                    onChange={(e) => onFormChange({...form, width: e.target.value})}
                  />
                  <span className='fw-bold'>x</span>
                  <Form.Control
                    type='number'
                    min='0'
                    step='0.01'
                    placeholder='H'
                    value={form.height}
                    onChange={(e) => onFormChange({...form, height: e.target.value})}
                  />
                  <Form.Select
                    value={form.dimensionUnit}
                    style={{maxWidth: '110px'}}
                    onChange={(e) => onFormChange({...form, dimensionUnit: e.target.value})}
                  >
                    {dimensionUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
              <div className='col-lg-4'>
                <label className='form-label fw-bold mb-2'>Weight</label>
                <div className='d-flex align-items-center gap-2'>
                  <Form.Control
                    type='number'
                    min='0'
                    step='0.01'
                    placeholder='0.00'
                    value={form.weight}
                    onChange={(e) => onFormChange({...form, weight: e.target.value})}
                  />
                  <Form.Select
                    value={form.weightUnit}
                    style={{maxWidth: '110px'}}
                    onChange={(e) => onFormChange({...form, weightUnit: e.target.value})}
                  >
                    {weightUnits.map((unit) => (
                      <option key={unit.value} value={unit.value}>
                        {unit.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              </div>
            </div>

            <div className='mt-7'>
              <h4 className='mb-4'>Items in this Package</h4>
              <div className='table-responsive border rounded'>
                <table className='table table-sm align-middle mb-0'>
                  <thead className='bg-light'>
                    <tr>
                      <th>Product Name</th>
                      <th>SKU</th>
                      <th>Barcode</th>
                      <th style={{minWidth: '160px'}}>QTY TO PACK</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.length ? (
                      lineItems.map((item, idx) => (
                        <tr key={`${item?.id || item?.sku || 'line'}-${idx}`}>
                          <td>{item?.name || item?.title || 'N/A'}</td>
                          <td>{item?.sku || item?.platform_product_variant?.sku || 'N/A'}</td>
                          <td>
                            {item?.barcode ||
                              item?.platform_product_variant?.barcode ||
                              item?.platform_product_variant?.gtin_number ||
                              'N/A'}
                          </td>
                          <td>
                            <Form.Control
                              value={item?.quantity ?? 0}
                              readOnly
                              disabled
                              aria-label='QTY TO PACK'
                            />
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={4} className='text-center text-muted py-6'>
                          No line items available.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant='primary' onClick={onSave} disabled={loading || saving}>
          {saving ? 'Saving...' : mode === 'update' ? 'Update Package' : 'Create Package'}
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default OrderPackageModal
